import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import dateTimeType from '@drivesally/sally-fw/lib/type/types/dateTime.type';
import {Button, Icon, Label} from '@drivesally/sally-ui';
import {grey} from '@drivesally/sally-ui/lib/libs/colors';

import getLocalTimeZoneAbbreviation from '../../../chaya/lib/getLocalTimeZoneAbbreviation.lib.chaya';
import TextArea from '../../../layout/components/TextArea/TextArea';

const formatNoteDate = (date) =>
  `${dateTimeType(date).format()} ${getLocalTimeZoneAbbreviation(date)}`.trim();

const NotesSection = styled.div`
  margin-top: 20px;
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 220px;
  overflow-y: auto;
`;

const NoteRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 8px;
`;

const NoteBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const NoteText = styled.div`
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

const NoteMeta = styled.div`
  color: #8ea1ac;
  font-size: 12px;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  flex-shrink: 0;
  padding: 4px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    color: #333;
  }
`;

const EmptyState = styled.div`
  color: #666;
  font-size: 14px;
`;

const NewNoteRow = styled.div`
  border-top: 1px solid ${grey};
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
`;

const CrmTicketNotes = ({
  creatingNote,
  disabled,
  newNoteText,
  notes,
  notesLoading,
  onCreateNote,
  onDeleteNote,
  onNewNoteTextChange,
  savingNoteIds,
}) => (
  <NotesSection>
    <Label label="Notes">
      <NotesList>
        {notesLoading ? (
          <EmptyState>Loading notes...</EmptyState>
        ) : !notes.length ? (
          <EmptyState>No notes yet.</EmptyState>
        ) : (
          notes.map((note) => (
            <NoteRow key={note.id}>
              <NoteBody>
                <NoteText>{note.text || '-'}</NoteText>
                <NoteMeta>
                  Created by {note.created_by || '-'}
                  {note.created_at ? ` on ${formatNoteDate(note.created_at)}` : ''}
                </NoteMeta>
              </NoteBody>
              <DeleteButton
                type="button"
                disabled={disabled || savingNoteIds.includes(note.id)}
                onClick={() => onDeleteNote(note.id)}
              >
                <Icon icon="delete" />
              </DeleteButton>
            </NoteRow>
          ))
        )}
      </NotesList>
      <NewNoteRow>
        <TextArea
          value={newNoteText}
          onChange={onNewNoteTextChange}
          textAreaProps={{
            disabled: disabled || creatingNote,
            placeholder: 'Add a note...',
          }}
        />
        <Button
          theme="grey"
          size="small"
          loading={creatingNote}
          disabled={disabled || creatingNote || !newNoteText.trim().length}
          onClick={onCreateNote}
        >
          Add Note
        </Button>
      </NewNoteRow>
    </Label>
  </NotesSection>
);

CrmTicketNotes.propTypes = {
  creatingNote: PropTypes.bool,
  disabled: PropTypes.bool,
  newNoteText: PropTypes.string,
  notes: PropTypes.array,
  notesLoading: PropTypes.bool,
  onCreateNote: PropTypes.func,
  onDeleteNote: PropTypes.func,
  onNewNoteTextChange: PropTypes.func,
  savingNoteIds: PropTypes.array,
};

export default CrmTicketNotes;
